/**
 * 可复用的弹窗组件
 * 使用方法：
 * const modal = new Modal({
 *   title: '提示框',
 *   content: '这是弹窗内容',
 *   buttons: [
 *     { text: '取消', type: 'cancel', action: () => console.log('取消') },
 *     { text: '确定', type: 'confirm', action: () => console.log('确定') }
 *   ]
 * });
 * modal.show();
 */
class Modal {
  constructor(options = {}) {
    this.options = {
      title: options.title || "提示框",
      content: options.content || "请输入内容",
      buttons: options.buttons || [{ text: "取消", type: "cancel" }],
      width: options.width || "400px",
      height: options.height || "auto",
      closeOnOverlay: options.closeOnOverlay !== false,
      closeOnEscape: options.closeOnEscape !== false,
      onClose: options.onClose || null,
      onOpen: options.onOpen || null,
    };

    this.isOpen = false;
    this.modalElement = null;
    this.overlayElement = null;

    this.createModal();
    this.bindEvents();
  }

  /**
   * 创建弹窗DOM结构
   */
  createModal() {
    // 创建遮罩层
    this.overlayElement = document.createElement("div");
    this.overlayElement.className = "modal-overlay";
    this.overlayElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: none;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;

    // 创建弹窗容器
    this.modalElement = document.createElement("div");
    this.modalElement.className = "modal-container";
    this.modalElement.style.cssText = `
      background-color: #ffffff;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      width: ${this.options.width};
      max-width: 90vw;
      max-height: 90vh;
      overflow: hidden;
      transform: scale(0.8);
      transition: transform 0.3s ease;
      font-family: var(--font-body-family);
    `;

    // 创建弹窗内容
    this.modalElement.innerHTML = `
      <div class="modal-header" style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #e5e5e5;
        background-color: #f8f9fa;
      ">
        <h3 class="modal-title" style="
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #000;
        ">${this.options.title}</h3>
        <button class="modal-close" style="
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background-color 0.2s ease;
        ">&times;</button>
      </div>
      
      <div class="modal-body" style="
        padding: 20px;
        color: #000;
        line-height: 1.5;
        max-height: 60vh;
        overflow-y: auto;
      ">${this.options.content}</div>
      
      <div class="modal-footer" style="
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 16px 20px;
        border-top: 1px solid #e5e5e5;
        background-color: #f8f9fa;
      ">
        ${this.renderButtons()}
      </div>
    `;

    // 将弹窗添加到遮罩层
    this.overlayElement.appendChild(this.modalElement);

    // 添加到页面
    document.body.appendChild(this.overlayElement);
  }

  /**
   * 渲染按钮
   */
  renderButtons() {
    return this.options.buttons
      .map((button, index) => {
        const buttonStyle = this.getButtonStyle(button.type);
        return `
        <button class="modal-button modal-button-${button.type}" 
                data-button-index="${index}"
                style="${buttonStyle}">
          ${button.text}
        </button>
      `;
      })
      .join("");
  }

  /**
   * 获取按钮样式
   */
  getButtonStyle(type) {
    const baseStyle = `
      padding: 8px 16px;
      border: 1px solid #ddd;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      min-width: 80px;
    `;

    switch (type) {
      case "confirm":
        return (
          baseStyle +
          `
          background-color: #007bff;
          color: white;
          border-color: #007bff;
        `
        );
      case "danger":
        return (
          baseStyle +
          `
          background-color: #dc3545;
          color: white;
          border-color: #dc3545;
        `
        );
      case "cancel":
      default:
        return (
          baseStyle +
          `
          background-color: #ffffff;
          color: #000;
          border-color: #ddd;
        `
        );
    }
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    // 关闭按钮事件
    const closeButton = this.modalElement.querySelector(".modal-close");
    if (closeButton) {
      closeButton.addEventListener("click", () => this.close());

      // 悬停效果
      closeButton.addEventListener("mouseenter", () => {
        closeButton.style.backgroundColor = "#e9ecef";
      });

      closeButton.addEventListener("mouseleave", () => {
        closeButton.style.backgroundColor = "transparent";
      });
    }

    // 遮罩层点击事件
    if (this.options.closeOnOverlay) {
      this.overlayElement.addEventListener("click", (e) => {
        if (e.target === this.overlayElement) {
          this.close();
        }
      });
    }

    // ESC键关闭事件
    if (this.options.closeOnEscape) {
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.close();
        }
      });
    }

    // 按钮点击事件
    const buttons = this.modalElement.querySelectorAll(".modal-button");
    buttons.forEach((button, index) => {
      button.addEventListener("click", () => {
        const buttonConfig = this.options.buttons[index];
        if (buttonConfig && buttonConfig.action) {
          buttonConfig.action();
        } else {
          // 如果不是取消按钮，默认关闭弹窗
          if (buttonConfig.type === "cancel") {
            this.close();
          }
        }
      });

      // 按钮悬停效果
      button.addEventListener("mouseenter", () => {
        if (button.classList.contains("modal-button-confirm")) {
          button.style.backgroundColor = "#0056b3";
        } else if (button.classList.contains("modal-button-danger")) {
          button.style.backgroundColor = "#c82333";
        } else {
          button.style.backgroundColor = "#f8f9fa";
        }
      });

      button.addEventListener("mouseleave", () => {
        if (button.classList.contains("modal-button-confirm")) {
          button.style.backgroundColor = "#007bff";
        } else if (button.classList.contains("modal-button-danger")) {
          button.style.backgroundColor = "#dc3545";
        } else {
          button.style.backgroundColor = "#ffffff";
        }
      });
    });
  }

  /**
   * 显示弹窗
   */
  show() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.overlayElement.style.display = "flex";

    // 触发动画
    requestAnimationFrame(() => {
      this.overlayElement.style.opacity = "1";
      this.modalElement.style.transform = "scale(1)";
    });

    // 阻止背景滚动
    document.body.style.overflow = "hidden";

    // 触发打开回调
    if (this.options.onOpen) {
      this.options.onOpen();
    }
  }

  /**
   * 关闭弹窗
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // 触发关闭动画
    this.overlayElement.style.opacity = "0";
    this.modalElement.style.transform = "scale(0.8)";

    // 动画完成后隐藏
    setTimeout(() => {
      this.overlayElement.style.display = "none";
      document.body.style.overflow = "";

      // 触发关闭回调
      if (this.options.onClose) {
        this.options.onClose();
      }
    }, 300);
  }

  /**
   * 更新弹窗内容
   */
  updateContent(content) {
    const bodyElement = this.modalElement.querySelector(".modal-body");
    if (bodyElement) {
      bodyElement.innerHTML = content;
    }
  }

  /**
   * 更新弹窗标题
   */
  updateTitle(title) {
    const titleElement = this.modalElement.querySelector(".modal-title");
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  /**
   * 销毁弹窗
   */
  destroy() {
    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
    }
    this.isOpen = false;
  }
}

// 导出到全局
window.Modal = Modal;
